import React, { useEffect, useState } from "react"
import { Col, Row, UncontrolledAlert, Spinner } from "reactstrap"
import api from "API/Api"
import { AvForm, AvField, AvRadioGroup, AvRadio } from "availity-reactstrap-validation"
import { Link, useParams } from "react-router-dom"

const EditCenter = () => {
  const { id } = useParams()
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [pageLoad, setPageLoad] = useState(true)
  const [center, setCenter] = useState(null)
  const [states, setStates] = useState([])
  const [plants, setPlants] = useState([])
  const [companies, setCompanies] = useState([])
  const [districts, setDistricts] = useState([])
  const [taluks, setTaluks] = useState([])
  const [cows, setCows] = useState([])
  const [buffalos, setBuffalos] = useState([])
  const [common, setCommon] = useState([])
  const [ratechartType, setRatechartType] = useState("")
  const [loadingDistricts, setLoadingDistricts] = useState(false)
  const [loadingTaluks, setLoadingTaluks] = useState(false)
  const [selectedCompany, setSelectedCompany] = useState("")

  useEffect(() => {
    const loadData = async () => {
      try {
        const [cResp, stResp, cowResp, bufResp, comResp, plantResp, compResp] = await Promise.all([
          api.get("center/" + id), api.get("states"),
          api.get("rate-chart-type/1"), api.get("rate-chart-type/2"), api.get("rate-chart-type/3"),
          api.get("plants"), api.get("companies")
        ])
        const cData = cResp.data.data?.[0] || cResp.data.data
        setCenter(cData)
        setRatechartType(String(cData.rateChartType || ""))
        setStates(Array.isArray(stResp.data.data) ? stResp.data.data : stResp.data.data?.data || [])
        setCows(Array.isArray(cowResp.data.data) ? cowResp.data.data : cowResp.data.data?.data || [])
        setBuffalos(Array.isArray(bufResp.data.data) ? bufResp.data.data : bufResp.data.data?.data || [])
        setCommon(Array.isArray(comResp.data.data) ? comResp.data.data : comResp.data.data?.data || [])
        const plantData = Array.isArray(plantResp.data.data) ? plantResp.data.data : plantResp.data.data?.data || []
        setPlants(plantData)
        const compData = Array.isArray(compResp.data.data) ? compResp.data.data : compResp.data.data?.data || []
        setCompanies(compData)

        // Derive company from the plant that this CC belongs to
        if (cData.plant_id) {
          const matchedPlant = plantData.find(p => String(p.id) === String(cData.plant_id))
          if (matchedPlant && matchedPlant.company_id) {
            setSelectedCompany(String(matchedPlant.company_id))
          }
        }
        // Auto-select if only one company
        if (compData.length === 1) setSelectedCompany(String(compData[0].id))

        if (cData.state) {
          const dstResp = await api.get("districts/" + cData.state)
          setDistricts(Array.isArray(dstResp.data.data) ? dstResp.data.data : dstResp.data.data?.data || [])
          if (cData.district) {
            const tlkResp = await api.get("taluks/" + cData.district)
            setTaluks(Array.isArray(tlkResp.data.data) ? tlkResp.data.data : tlkResp.data.data?.data || [])
          }
        }
      } catch (e) { console.error(e) }
      setPageLoad(false)
    }
    loadData()
  }, [id])

  const stateChangeHandler = async e => {
    const stateId = e.target.value; setDistricts([]); setTaluks([])
    if (!stateId) return; setLoadingDistricts(true)
    try { const r = await api.get("districts/" + stateId); setDistricts(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || []) } catch (e) { console.error(e) }
    setLoadingDistricts(false)
  }
  const districtChangeHandler = async e => {
    const districtId = e.target.value; setTaluks([])
    if (!districtId) return; setLoadingTaluks(true)
    try { const r = await api.get("taluks/" + districtId); setTaluks(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || []) } catch (e) { console.error(e) }
    setLoadingTaluks(false)
  }

  const handleValidSubmit = async (event, values) => {
    setLoading(true)
    try {
      if (ratechartType === "0") { values.cowRatechart = 0; values.buffaloRatechart = 0 }
      else if (ratechartType === "1") { values.bothRatechart = 0 }
      // Remove company_id — not a column in chilling_centers table
      delete values.company_id
      const resp = await api.put("update-center/" + id, values)
      if (resp.data.success) { setSubmitted(true); setTimeout(() => setSubmitted(false), 4000) }
    } catch (e) { console.error(e) }
    setLoading(false)
  }

  if (pageLoad) return <div className="dashBody"><div className="text-center py-5"><Spinner color="primary" style={{ width: "3rem", height: "3rem" }} /><p className="mt-3 text-muted">Loading Center...</p></div></div>
  if (!center) return <div className="dashBody"><p>Center not found</p></div>

  const hasMultiCompanies = companies.length > 1
  // Filter plants by selected company
  const filteredPlants = hasMultiCompanies && selectedCompany
    ? plants.filter(p => String(p.company_id) === selectedCompany)
    : plants

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader">
        <Row>
          <Col md={6}><h4>Edit Chilling Center</h4></Col>
          <Col md={6} style={{ textAlign: "right" }}>
            <Link to="/chilling-centers" className="dashBtn"><i className="fas fa-chevron-left"></i> <b>Back</b></Link>
          </Col>
        </Row>
      </div>
      <div className="dashContent">
        <AvForm className="form-horizontal mt-4" onValidSubmit={handleValidSubmit}>

          {/* Center Info */}
          <h6 style={{ color: "#6366f1", borderBottom: "2px solid #e2e8f0", paddingBottom: "8px", marginBottom: "16px" }}>
            <i className="fas fa-warehouse"></i> Center Information
          </h6>
          <Row>
            {hasMultiCompanies && (
              <Col md={3}><div className="mb-3">
                <AvField type="select" name="company_id" label="Company *" required value={selectedCompany}
                  onChange={e => setSelectedCompany(e.target.value)}>
                  <option value="">Select Company</option>
                  {companies.map((c, i) => <option key={i} value={String(c.id)}>{c.name}</option>)}
                </AvField>
              </div></Col>
            )}
            <Col md={hasMultiCompanies ? 3 : 4}><div className="mb-3">
              <AvField type="select" name="plant_id" label="Plant *" required value={String(center.plant_id || "")}>
                <option value="">Choose a client plant name . . .</option>
                {filteredPlants.map((p, i) => <option key={i} value={String(p.id)}>{p.name}</option>)}
              </AvField>
            </div></Col>
            <Col md={hasMultiCompanies ? 3 : 4}><div className="mb-3">
              <AvField name="name" label="Chilling Center Name *" value={center.name} type="text" required />
            </div></Col>
            <Col md={hasMultiCompanies ? 3 : 4}><div className="mb-3">
              <AvField name="number" label="Chilling Center Number *" value={center.number} type="text" required />
            </div></Col>
          </Row>

          {/* Address */}
          <h6 style={{ color: "#0ea5e9", borderBottom: "2px solid #e2e8f0", paddingBottom: "8px", marginBottom: "16px", marginTop: "12px" }}>
            <i className="fas fa-map-marker-alt"></i> Address Details
          </h6>
          <Row>
            <Col md={3}><div className="mb-3">
              <AvField name="door" label="Door No" value={center.door} type="text" />
            </div></Col>
            <Col md={3}><div className="mb-3">
              <AvField name="village" label="Village Name" value={center.village} type="text" />
            </div></Col>
            <Col md={3}><div className="mb-3">
              <AvField name="talukName" label="Taluk Name" value={center.taluk || center.talukName} type="text" />
            </div></Col>
            <Col md={3}><div className="mb-3">
              <AvField type="select" name="state" label="State" value={String(center.state || "")} onChange={stateChangeHandler}>
                <option value="">Select State</option>
                {states.map((s, i) => <option key={i} value={String(s.id)}>{s.state}</option>)}
              </AvField>
            </div></Col>
          </Row>
          <Row>
            <Col md={4}><div className="mb-3">
              <AvField type="select" name="district" label={<>District {loadingDistricts && <Spinner size="sm" color="primary" className="ms-1" />}</>} value={String(center.district || "")} onChange={districtChangeHandler}>
                <option value="">{loadingDistricts ? "Loading..." : districts.length ? "Select District" : "Select State first"}</option>
                {districts.map((d, i) => <option key={i} value={String(d.id)}>{d.district}</option>)}
              </AvField>
            </div></Col>
            <Col md={4}><div className="mb-3">
              <AvField name="pincode" label="Pincode" value={center.pincode} type="text"
                validate={{
                  pattern: { value: /^[1-9][0-9]{5}$/, errorMessage: "Enter valid 6-digit pin code" },
                  minLength: { value: 6, errorMessage: "Pin code must be 6 digits" },
                  maxLength: { value: 6, errorMessage: "Pin code must be 6 digits" }
                }} />
            </div></Col>
          </Row>

          {/* App Logins */}
          <h6 style={{ color: "#22c55e", borderBottom: "2px solid #e2e8f0", paddingBottom: "8px", marginBottom: "16px", marginTop: "12px" }}>
            <i className="fas fa-mobile-alt"></i> App Login Details
          </h6>
          <Row>
            <Col md={6}><div className="mb-3">
              <AvField value={center.qualityAppLogins} label="Quality App Remote Login Details" type="textarea" name="qualityAppLogins" rows="3" />
            </div></Col>
            <Col md={6}><div className="mb-3">
              <AvField value={center.remoteAppLogins} label="Reception App Remote Login Details" type="textarea" name="remoteAppLogins" rows="3" />
            </div></Col>
          </Row>

          {/* Rate Chart Config */}
          <h6 style={{ color: "#f59e0b", borderBottom: "2px solid #e2e8f0", paddingBottom: "8px", marginBottom: "16px", marginTop: "12px" }}>
            <i className="fas fa-chart-line"></i> Rate Chart Configuration
          </h6>
          <Row>
            <Col md={6}><div className="mb-3">
              <AvRadioGroup inline name="ccSolutionInstalled" label="Is NextGenDairy CC Solution Installed for this CC? *" required errorMessage="Pick one!" value={center.ccSolutionInstalled}>
                <AvRadio label="Yes" value="yes" /><AvRadio label="No" value="no" />
              </AvRadioGroup>
            </div></Col>
            <Col md={6}><div className="mb-3">
              <AvRadioGroup inline name="rateChartVlcc" label="Rate Chart Applicable for VLCC Entry *" required errorMessage="Pick one!" value={center.rateChartVlcc}>
                <AvRadio label="Common For All Centers" value="Common For All Centers" />
                <AvRadio label="VLCC's Own Rate Chart" value="VLCC's Own Rate Chart" />
                <AvRadio label="Centerwise" value="Centerwise" />
              </AvRadioGroup>
            </div></Col>
          </Row>
          <Row>
            <Col md={6}><div className="mb-3">
              <AvRadioGroup inline name="rateChartBulkVendor" label="Rate Chart Applicable for Bulk Vendor Entry *" required errorMessage="Pick one!" value={center.rateChartBulkVendor}>
                <AvRadio label="Common for All Vendors" value="Common for All Vendors" />
                <AvRadio label="Vendorwise" value="Vendorwise" />
              </AvRadioGroup>
            </div></Col>
            <Col md={6}><div className="mb-3">
              <AvRadioGroup inline name="rateChartType" label="Rate Chart Type *" required errorMessage="Pick one!" value={String(center.rateChartType || "")} onChange={e => setRatechartType(e.target.value)}>
                <AvRadio label="Common" value="0" /><AvRadio label="Separate" value="1" />
              </AvRadioGroup>
            </div></Col>
          </Row>
          <Row>
            {ratechartType === "1" ? (<>
              <Col md={6}><div className="mb-3">
                <AvField type="select" name="cowRatechart" label="Cow Milk Ratechart *" required value={String(center.cowRatechart || "")}>
                  <option value="">Select Cow Milk Ratechart</option>
                  {cows.map((c, i) => <option key={i} value={String(c.id)}>{c.ratechartName}</option>)}
                </AvField>
              </div></Col>
              <Col md={6}><div className="mb-3">
                <AvField type="select" name="buffaloRatechart" label="Buffalo Milk Ratechart *" required value={String(center.buffaloRatechart || "")}>
                  <option value="">Select Buffalo Milk Ratechart</option>
                  {buffalos.map((b, i) => <option key={i} value={String(b.id)}>{b.ratechartName}</option>)}
                </AvField>
              </div></Col>
            </>) : ratechartType === "0" ? (
              <Col md={6}><div className="mb-3">
                <AvField type="select" name="bothRatechart" label="Select Common Rate Chart" value={String(center.bothRatechart || "")}>
                  <option value="">Choose a common rate chart . . .</option>
                  {common.map((c, i) => <option key={i} value={String(c.id)}>{c.ratechartName}</option>)}
                </AvField>
              </div></Col>
            ) : null}
          </Row>

          {/* Sour Milk & Incentives */}
          <h6 style={{ color: "#8b5cf6", borderBottom: "2px solid #e2e8f0", paddingBottom: "8px", marginBottom: "16px", marginTop: "12px" }}>
            <i className="fas fa-rupee-sign"></i> Sour Milk & Incentives
          </h6>
          <Row>
            <Col md={12}><div className="mb-3">
              <AvRadioGroup inline name="sourMilkRateType" label="Sour Milk Rate Calculation Type *" required errorMessage="Pick one!" value={center.sourMilkRateType}>
                <AvRadio label="Flat Rate" value="Flat Rate" />
                <AvRadio label="Use Same Rate Chart as Good Milk" value="Use Same Rate Chart as Good Milk" />
              </AvRadioGroup>
            </div></Col>
          </Row>
          <Row>
            <Col md={6}><div className="mb-3">
              <AvRadioGroup inline name="cowMilkIncentive" label="Cow Milk Incentive/Commission Available? *" required errorMessage="Pick one!" value={center.cowMilkIncentive}>
                <AvRadio label="Not Available" value="Not Available" />
                <AvRadio label="Common for All Vendors/Agents" value="Common for All Vendors/Agents" />
                <AvRadio label="Vendorwise/Agentwise Incentive" value="Vendorwise/Agentwise Incentive" />
              </AvRadioGroup>
            </div></Col>
            <Col md={6}><div className="mb-3">
              <AvRadioGroup inline name="buffaloMilkIncentive" label="Buffalo Milk Incentive/Commission Available? *" required errorMessage="Pick one!" value={center.buffaloMilkIncentive}>
                <AvRadio label="Not Available" value="Not Available" />
                <AvRadio label="Common for All Vendors/Agents" value="Common for All Vendors/Agents" />
                <AvRadio label="Vendorwise/Agentwise Incentive" value="Vendorwise/Agentwise Incentive" />
              </AvRadioGroup>
            </div></Col>
          </Row>

          {/* Management */}
          <h6 style={{ color: "#ef4444", borderBottom: "2px solid #e2e8f0", paddingBottom: "8px", marginBottom: "16px", marginTop: "12px" }}>
            <i className="fas fa-user-tie"></i> Management
          </h6>
          <Row>
            <Col md={4}><div className="mb-3">
              <AvField name="managerFields" label="Procurement Manager (Fields)" value={center.managerFields} type="text" />
            </div></Col>
            <Col md={4}><div className="mb-3">
              <AvField name="managerOperations" label="Procurement Manager (Operations)" value={center.managerOperations} type="text" />
            </div></Col>
            <Col md={4}><div className="mb-3">
              <AvField name="ccIncharges" label="CC Incharges" value={center.ccIncharges} type="text" />
            </div></Col>
          </Row>

          <div className="mb-3 row mt-4">
            <div className="col-6">
              {submitted && <UncontrolledAlert color="success"><strong>Chilling Center Updated!</strong></UncontrolledAlert>}
            </div>
            <div className="col-6 text-end">
              <button className="btn btn-primary w-md waves-effect waves-light" type="submit" disabled={loading}>
                {loading ? <><Spinner size="sm" /><span style={{ paddingLeft: "10px" }}>Updating...</span></> : <span><i className="fas fa-save"></i> Update Center</span>}
              </button>
            </div>
          </div>
        </AvForm>
      </div>
    </div>
  )
}

export default EditCenter
